import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Check, X, Mail, User, Lock, ArrowRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import './Signup.scss';

interface PlanInfo {
  id: string;
  name: string;
  price: string;
  period: string;
}

const Signup: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { register, isLoading, error, clearError } = useAuth();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  // Get plan information from query params
  const planId = searchParams.get('plan');
  const returnUrl = searchParams.get('return');
  
  const planInfo: PlanInfo | null = planId ? {
    id: planId,
    name: planId === 'free' ? 'Starter' : planId === 'pro' ? 'Professional' : 'Enterprise',
    price: planId === 'free' ? 'Free' : planId === 'pro' ? '$25' : '$120',
    period: planId === 'free' ? 'forever' : 'per month'
  } : null;

  const validatePassword = (password: string) => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    return requirements;
  };

  const passwordRequirements = validatePassword(formData.password);
  const isPasswordValid = Object.values(passwordRequirements).every(req => req);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear specific error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    // Clear auth error when user starts typing
    if (error) {
      clearError();
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!isPasswordValid) {
      newErrors.password = 'Password does not meet requirements';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!termsAccepted) {
      newErrors.terms = 'You must accept the terms of service';
    }

    if (!privacyAccepted) {
      newErrors.privacy = 'You must accept the privacy policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      await register({
        email: formData.email,
        password: formData.password,
        first_name: formData.firstName,
        last_name: formData.lastName,
        terms_accepted: termsAccepted,
        privacy_accepted: privacyAccepted
      });
      
      // Redirect to appropriate page after successful registration
      const redirectUrl = returnUrl || '/dashboard';
      navigate(redirectUrl);
      
    } catch (error) {
      console.error('Signup error:', error);
      // Error is handled by the auth context
    }
  };

  return (
    <div className="signup">
      <div className="signup__container">
        <div className="signup__content">
          {/* Left side - Form */}
          <div className="signup__form-section">
            <div className="signup__header">
              <h1 className="signup__title">
                Create Your <span className="gradient-text">CoinVAT</span> Account
              </h1>
              <p className="signup__subtitle">
                Join thousands of traders who trust CoinVAT with their automated trading
              </p>
            </div>

            {planInfo && (
              <div className="signup__plan-info">
                <div className="signup__plan-badge">
                  <span className="signup__plan-label">Selected Plan:</span>
                  <span className="signup__plan-name">{planInfo.name}</span>
                  <span className="signup__plan-price">
                    {planInfo.price}/{planInfo.period}
                  </span>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="signup__form">
              {error && (
                <div className="signup__error signup__error--general">
                  {error}
                </div>
              )}

              <div className="signup__form-row">
                <div className="signup__form-group">
                  <label htmlFor="firstName" className="signup__label">
                    <User size={16} />
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`signup__input ${errors.firstName ? 'signup__input--error' : ''}`}
                    placeholder="Enter your first name"
                  />
                  {errors.firstName && (
                    <span className="signup__error">{errors.firstName}</span>
                  )}
                </div>

                <div className="signup__form-group">
                  <label htmlFor="lastName" className="signup__label">
                    <User size={16} />
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`signup__input ${errors.lastName ? 'signup__input--error' : ''}`}
                    placeholder="Enter your last name"
                  />
                  {errors.lastName && (
                    <span className="signup__error">{errors.lastName}</span>
                  )}
                </div>
              </div>

              <div className="signup__form-group">
                <label htmlFor="email" className="signup__label">
                  <Mail size={16} />
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`signup__input ${errors.email ? 'signup__input--error' : ''}`}
                  placeholder="Enter your email address"
                />
                {errors.email && (
                  <span className="signup__error">{errors.email}</span>
                )}
              </div>

              <div className="signup__form-group">
                <label htmlFor="password" className="signup__label">
                  <Lock size={16} />
                  Password
                </label>
                <div className="signup__password-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`signup__input ${errors.password ? 'signup__input--error' : ''}`}
                    placeholder="Create a strong password"
                  />
                  <button
                    type="button"
                    className="signup__password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                
                {formData.password && (
                  <div className="signup__password-requirements">
                    <div className={`signup__requirement ${passwordRequirements.length ? 'signup__requirement--valid' : ''}`}>
                      {passwordRequirements.length ? <Check size={12} /> : <X size={12} />}
                      <span>At least 8 characters</span>
                    </div>
                    <div className={`signup__requirement ${passwordRequirements.uppercase ? 'signup__requirement--valid' : ''}`}>
                      {passwordRequirements.uppercase ? <Check size={12} /> : <X size={12} />}
                      <span>One uppercase letter</span>
                    </div>
                    <div className={`signup__requirement ${passwordRequirements.lowercase ? 'signup__requirement--valid' : ''}`}>
                      {passwordRequirements.lowercase ? <Check size={12} /> : <X size={12} />}
                      <span>One lowercase letter</span>
                    </div>
                    <div className={`signup__requirement ${passwordRequirements.number ? 'signup__requirement--valid' : ''}`}>
                      {passwordRequirements.number ? <Check size={12} /> : <X size={12} />}
                      <span>One number</span>
                    </div>
                    <div className={`signup__requirement ${passwordRequirements.special ? 'signup__requirement--valid' : ''}`}>
                      {passwordRequirements.special ? <Check size={12} /> : <X size={12} />}
                      <span>One special character</span>
                    </div>
                  </div>
                )}
                
                {errors.password && (
                  <span className="signup__error">{errors.password}</span>
                )}
              </div>

              <div className="signup__form-group">
                <label htmlFor="confirmPassword" className="signup__label">
                  <Lock size={16} />
                  Confirm Password
                </label>
                <div className="signup__password-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`signup__input ${errors.confirmPassword ? 'signup__input--error' : ''}`}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    className="signup__password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <span className="signup__error">{errors.confirmPassword}</span>
                )}
              </div>

              <div className="signup__checkboxes">
                <div className="signup__checkbox-group">
                  <label className="signup__checkbox">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                    />
                    <span className="signup__checkbox-mark"></span>
                    <span className="signup__checkbox-label">
                      I agree to the{' '}
                      <Link to="/terms" className="signup__link">Terms of Service</Link>
                    </span>
                  </label>
                  {errors.terms && (
                    <span className="signup__error">{errors.terms}</span>
                  )}
                </div>

                <div className="signup__checkbox-group">
                  <label className="signup__checkbox">
                    <input
                      type="checkbox"
                      checked={privacyAccepted}
                      onChange={(e) => setPrivacyAccepted(e.target.checked)}
                    />
                    <span className="signup__checkbox-mark"></span>
                    <span className="signup__checkbox-label">
                      I agree to the{' '}
                      <Link to="/privacy" className="signup__link">Privacy Policy</Link>
                    </span>
                  </label>
                  {errors.privacy && (
                    <span className="signup__error">{errors.privacy}</span>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="signup__submit btn btn-primary btn-lg"
              >
                {isLoading ? (
                  <span>Creating Account...</span>
                ) : (
                  <>
                    <span>Create Account</span>
                    <ArrowRight size={16} />
                  </>
                )}
              </button>

              <div className="signup__terms">
                <p>
                  By creating an account, you confirm that you have read and accepted our terms and privacy policy.
                </p>
              </div>
            </form>

            <div className="signup__footer">
              <p>
                Already have an account?{' '}
                <Link 
                  to={`/login${planId ? `?plan=${planId}` : ''}${returnUrl ? `&return=${returnUrl}` : ''}`} 
                  className="signup__link"
                >
                  Sign in here
                </Link>
              </p>
            </div>
          </div>

          {/* Right side - Benefits */}
          <div className="signup__benefits-section">
            <div className="signup__benefits">
              <h2 className="signup__benefits-title">Why Choose CoinVAT?</h2>
              
              <div className="signup__benefit">
                <div className="signup__benefit-icon">
                  <Check size={20} />
                </div>
                <div className="signup__benefit-content">
                  <h3>Advanced AI Technology</h3>
                  <p>Our proprietary algorithms analyze market data 24/7 to make optimal trading decisions</p>
                </div>
              </div>

              <div className="signup__benefit">
                <div className="signup__benefit-icon">
                  <Check size={20} />
                </div>
                <div className="signup__benefit-content">
                  <h3>Bank-Grade Security</h3>
                  <p>Your funds and data are protected with enterprise-level security measures</p>
                </div>
              </div>

              <div className="signup__benefit">
                <div className="signup__benefit-icon">
                  <Check size={20} />
                </div>
                <div className="signup__benefit-content">
                  <h3>Transparent Fees</h3>
                  <p>No hidden charges - you only pay fees on profitable trades</p>
                </div>
              </div>

              <div className="signup__benefit">
                <div className="signup__benefit-icon">
                  <Check size={20} />
                </div>
                <div className="signup__benefit-content">
                  <h3>24/7 Support</h3>
                  <p>Our expert team is here to help you succeed with round-the-clock support</p>
                </div>
              </div>
            </div>

            <div className="signup__trust-indicators">
              <div className="signup__trust-item">
                <strong>50,000+</strong>
                <span>Active Traders</span>
              </div>
              <div className="signup__trust-item">
                <strong>$2.5B+</strong>
                <span>Volume Traded</span>
              </div>
              <div className="signup__trust-item">
                <strong>99.9%</strong>
                <span>Uptime</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
